import React, { useEffect } from "react"
import {
    Box,
    Button,
    Checkbox,
    FormControlLabel,
    Grid,
    Paper,
    TextField,
    Tooltip,
    Typography,
} from "@material-ui/core"
import { makeStyles } from "@material-ui/core/styles"
import InfoIcon from "@material-ui/icons/Info"
import SaveIcon from "@material-ui/icons/Save"
import TuneIcon from "@material-ui/icons/Tune"
import { useSnackbar } from "../../../utils/notificacoes"
import { ParametrosAvancadosActionsTypes } from "../context/parametrosAvancadosActions"
import { useParametrosAvancadosContext } from "../context/useParametrosAvancadosContext"
import { GRUPOS_CHECKBOXES } from "../models/parametrosAvancadosModel"
import type { ParametrosAvancadosModel } from "../models/parametrosAvancadosModel"

const useStyles = makeStyles(theme => ({
    container: {
        paddingLeft: "90px",
        paddingRight: "10px",
        paddingTop: "100px",
        paddingBottom: "30px",
        [theme.breakpoints.down("md")]: { paddingLeft: "20px", paddingRight: "20px", paddingTop: "80px" },
        [theme.breakpoints.down("sm")]: { paddingLeft: "15px", paddingRight: "15px", paddingTop: "70px" },
    },
    content: {
        margin: "auto",
        maxWidth: "800px",
        [theme.breakpoints.down("sm")]: { maxWidth: "100%" },
    },
    configCard: {
        borderRadius: 20,
        boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
        backgroundColor: "white",
        overflow: "hidden",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { borderRadius: 15, marginBottom: theme.spacing(2) },
    },
    cardHeader: {
        backgroundColor: "#f8f9fa",
        padding: theme.spacing(2.5),
        borderBottom: "2px solid #e9ecef",
        display: "flex",
        alignItems: "center",
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    cardHeaderTitle: {
        fontWeight: 600,
        color: "#2c3e50",
        [theme.breakpoints.down("sm")]: { fontSize: "1.1rem" },
    },
    cardContent: {
        padding: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { padding: theme.spacing(2) },
    },
    iconHeader: {
        color: "#4caf50",
        marginRight: theme.spacing(1),
    },
    titleText: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(3),
        [theme.breakpoints.down("sm")]: { fontSize: "1.5rem" },
        [theme.breakpoints.down("xs")]: { fontSize: "1.25rem" },
    },
    saveButton: {
        borderRadius: 25,
        backgroundColor: "#4caf50",
        color: "white",
        fontWeight: 600,
        padding: "10px 32px",
        boxShadow: "0 4px 12px rgba(76,175,80,0.3)",
        "&:hover": { backgroundColor: "#43a047", boxShadow: "0 6px 16px rgba(76,175,80,0.4)" },
    },
    checkboxLabel: {
        color: "#2c3e50",
    },
    checkboxRow: {
        display: "flex",
        alignItems: "center",
    },
    infoIcon: {
        fontSize: 16,
        color: "#90a4ae",
        marginLeft: theme.spacing(0.5),
        cursor: "pointer",
    },
    grupoTitulo: {
        fontWeight: 600,
        color: "#2c3e50",
        marginBottom: theme.spacing(1),
        borderBottom: "1px solid #e9ecef",
        paddingBottom: theme.spacing(0.5),
    },
}))

export function FormularioParametrosAvancados() {
    const classes = useStyles()
    const { parametros, dispatch, salvar, erroCarregamento } = useParametrosAvancadosContext()
    const { enqueueSnackbar } = useSnackbar()

    const alertStart = (msg: string, tipo: "success" | "error" | "info") => {
        enqueueSnackbar(msg, { variant: tipo })
    }

    useEffect(() => {
        if (erroCarregamento) alertStart(erroCarregamento, "error")
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [erroCarregamento])

    const handleCampo = (campo: keyof ParametrosAvancadosModel, valor: string | number | boolean | null) => {
        dispatch({ type: ParametrosAvancadosActionsTypes.ATUALIZAR_CAMPO, payload: { campo, valor } })
    }

    const handleSalvar = async () => {
        try {
            await salvar()
            alertStart("Parâmetros avançados salvos com sucesso!", "success")
        } catch (e) {
            alertStart((e as Error).message || "Erro ao salvar", "error")
        }
    }

    return (
        <Box className={classes.container}>
            <Box className={classes.content}>
                <Typography variant="h5" className={classes.titleText}>
                    Configuração de Delivery
                </Typography>

                {/* Campos numéricos e de texto */}
                <Paper className={classes.configCard}>
                    <Box className={classes.cardHeader}>
                        <TuneIcon className={classes.iconHeader} />
                        <Typography variant="h6" className={classes.cardHeaderTitle}>
                            Parâmetros Avançados
                        </Typography>
                    </Box>
                    <Box className={classes.cardContent}>
                        <Grid container spacing={3}>
                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Fuso horário (UTC offset)"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: -12, max: 14, step: 1 }}
                                    value={parametros.fusoHorario ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("fusoHorario", v === "" ? null : parseInt(v))
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Hora início turno dia"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 23, step: 1 }}
                                    value={parametros.horaInicioTurnoDia ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("horaInicioTurnoDia", v === "" ? null : parseInt(v))
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="Hora início turno noite"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 23, step: 1 }}
                                    value={parametros.horaInicioTurnoNoite ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("horaInicioTurnoNoite", v === "" ? null : parseInt(v))
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6} md={4}>
                                <TextField
                                    label="% Máx. bebidas pagamento online"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    type="number"
                                    inputProps={{ min: 0, max: 100, step: 1 }}
                                    value={parametros.percentualMaximoBebidasPagamentoOnline ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo(
                                            "percentualMaximoBebidasPagamentoOnline",
                                            v === "" ? null : parseInt(v)
                                        )
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="URL WebHook de integração"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 500 }}
                                    value={parametros.urlWebHookIntegracao ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("urlWebHookIntegracao", v === "" ? null : v)
                                    }}
                                />
                            </Grid>

                            <Grid item xs={12} sm={6}>
                                <TextField
                                    label="Auth WebHook de integração"
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    inputProps={{ maxLength: 500 }}
                                    value={parametros.authWebHookIntegracao ?? ""}
                                    onChange={e => {
                                        const v = e.target.value
                                        handleCampo("authWebHookIntegracao", v === "" ? null : v)
                                    }}
                                />
                            </Grid>
                        </Grid>
                    </Box>
                </Paper>

                {/* Grupos de checkboxes */}
                {GRUPOS_CHECKBOXES.map(grupo => (
                    <Paper key={grupo.titulo} className={classes.configCard}>
                        <Box className={classes.cardHeader}>
                            <Typography variant="h6" className={classes.cardHeaderTitle}>
                                {grupo.titulo}
                            </Typography>
                        </Box>
                        <Box className={classes.cardContent}>
                            <Grid container spacing={1}>
                                {grupo.campos.map(meta => (
                                    <Grid key={meta.campo} item xs={12} sm={6}>
                                        <Box className={classes.checkboxRow}>
                                            <FormControlLabel
                                                className={classes.checkboxLabel}
                                                control={
                                                    <Checkbox
                                                        checked={parametros[meta.campo] as boolean}
                                                        onChange={e => {
                                                            const checked = e.target.checked
                                                            handleCampo(meta.campo, checked)
                                                        }}
                                                        style={{ color: "#4caf50" }}
                                                    />
                                                }
                                                label={meta.label}
                                            />
                                            {meta.descricao && (
                                                <Tooltip title={meta.descricao} placement="right">
                                                    <InfoIcon className={classes.infoIcon} />
                                                </Tooltip>
                                            )}
                                        </Box>
                                    </Grid>
                                ))}
                            </Grid>
                        </Box>
                    </Paper>
                ))}

                <Box display="flex" justifyContent="center" mb={4}>
                    <Button
                        className={classes.saveButton}
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={handleSalvar}
                    >
                        Salvar
                    </Button>
                </Box>
            </Box>
        </Box>
    )
}
